import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Sparkles, ArrowRight, X, ChevronDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTemplate } from '@/context/TemplateContext';
import { useDisplaySettings } from '@/hooks/useDisplaySettings';
import { WeatherWidget } from './WeatherWidget';
import { templateList } from '@/config/templates';
import DynamicIcon from './DynamicIcon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

interface PromoBannerProps {
  variant?: 'banner' | 'card' | 'floating';
  className?: string;
}

export const PromoBanner: React.FC<PromoBannerProps> = ({ variant = 'banner', className = '' }) => {
  const { template, templateId, setTemplateId, previewMode } = useTemplate();
  const { settings } = useDisplaySettings();
  const [isVisible, setIsVisible] = React.useState(true);

  // Check display settings based on variant
  if (variant === 'floating' && !settings.show_floating_promo) return null;
  if (variant === 'banner' && !settings.show_banner_promo) return null;
  
  if (!isVisible) return null;

  const currentTemplate = templateList.find(t => t.id === templateId);
  const showTemplateSwitcher = settings.show_template_switcher && !previewMode;

  if (variant === 'floating') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 2, duration: 0.4, ease: 'easeOut' }}
        className={`fixed bottom-20 left-4 z-40 w-72 ${className}`}
      >
        <div className="relative bg-background rounded-2xl shadow-xl border border-border overflow-hidden">
          {/* Accent strip */}
          <div className="h-1 bg-gradient-to-r from-primary via-primary/80 to-primary/40" />

          <button
            onClick={() => setIsVisible(false)}
            className="absolute top-3 right-3 w-6 h-6 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-3 w-3 text-muted-foreground" />
          </button>

          <div className="px-5 pt-4 pb-5">
            <div className="flex items-center gap-2.5 mb-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Gift className="h-4 w-4 text-primary" />
              </div>
              <p className="font-heading font-semibold text-sm text-foreground leading-tight">
                New Customer Special
              </p>
            </div>
            <p className="text-muted-foreground text-xs leading-relaxed mb-4">
              Get 15% off your first service. Limited time — claim yours today.
            </p>
            <Button asChild size="sm" className="w-full gap-1.5 text-xs h-9">
              <Link to="/promos">
                View Promos
                <ArrowRight className="h-3 w-3" />
              </Link>
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  if (variant === 'card') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        className={`bg-gradient-to-br from-primary via-primary to-accent p-6 rounded-xl text-primary-foreground ${className}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5" />
          <span className="font-semibold">New Customer Offer</span>
        </div>
        <h3 className="font-heading text-2xl font-bold mb-2">15% Off Your First Service</h3>
        <p className="text-primary-foreground/80 text-sm mb-4">
          Welcome to {template.brand.name}! As a new customer, enjoy exclusive savings on your first project.
        </p>
        <Button asChild variant="secondary">
          <Link to="/contact?promo=new-customer">
            Claim This Offer <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </Button>
      </motion.div>
    );
  }

  // Default banner variant
  return (
    <div className={`bg-gradient-to-r from-primary to-accent text-primary-foreground py-2 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between gap-4">
          {/* Weather Widget on the left */}
          <div className="hidden sm:flex items-center min-w-[120px]">
            <WeatherWidget variant="compact" className="text-primary-foreground" />
          </div>
          
          {/* Promo content in the center */}
          <div className="flex items-center justify-center gap-4 flex-wrap flex-1">
            <div className="flex items-center gap-2">
              <Gift className="h-4 w-4" />
              <span className="font-medium text-sm">
                🎉 New Customer Special: 15% Off Your First Service!
              </span>
            </div>
            <Button asChild size="sm" variant="secondary" className="h-7 text-xs">
              <Link to="/contact?promo=new-customer">
                Claim Offer <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </Button>
          </div>
          
          {/* Template Switcher on the right */}
          <div className="hidden sm:flex items-center min-w-[120px] justify-end">
            {showTemplateSwitcher && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors text-xs font-medium">
                    <DynamicIcon name={currentTemplate?.icon || 'HardHat'} className="h-3.5 w-3.5" />
                    <span className="hidden lg:inline">{currentTemplate?.name}</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    Choose a Pro Template
                  </div>
                  <DropdownMenuSeparator />
                  {templateList.map((t) => (
                    <DropdownMenuItem
                      key={t.id}
                      onClick={() => setTemplateId(t.id)}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <DynamicIcon name={t.icon} className="h-4 w-4" />
                      <span className="flex-1">{t.name}</span>
                      {templateId === t.id && <Check className="h-4 w-4 text-primary" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
